<mat-toolbar class="content-header">
  <span class="title">
    <mat-icon class="title-icon">account_circle</mat-icon>
    {{"title.account" | translate}}
  </span>
  <span class="toolbar-spacer"></span>
</mat-toolbar>

<div class="profile-container" *ngIf="form">
  <form [formGroup]="form" class="form">
    <div class="settings-layout">

      <!-- Sidebar -->
      <aside class="settings-sidebar">
        <div class="sidebar-user">
          <app-user-avatar [avatarUrl]="user.avatar" [firstName]="user.firstName" [lastName]="user.lastName"
            [size]="36" [showStatus]="false">
          </app-user-avatar>

          <div class="sidebar-user-text">
            <div class="sidebar-user-name">{{ user.firstName }} {{ user.lastName }}</div>
            <div class="sidebar-user-role">
              {{ user.job }}<ng-container *ngIf="user.company"> · {{ user.company }}</ng-container>
            </div>
          </div>
        </div>

        <nav class="settings-nav">
          <ng-container *ngFor="let group of groupedSections()">
            <p class="nav-group-label">{{ group.labelKey | translate }}</p>

            <button type="button" class="nav-item" *ngFor="let section of group.sections"
              [class.active]="activeSection() === section.id" (click)="activeSection.set(section.id)">
              <mat-icon>{{ section.icon }}</mat-icon>
              <span>{{ section.labelKey | translate }}</span>
              <lib-chip *ngIf="section.badgeKey" [text]="section.badgeKey | translate" color="primary" size="small">
              </lib-chip>
            </button>
          </ng-container>
        </nav>

        <div class="sidebar-footer">
          <button type="button" class="nav-item danger" (click)="logout()">
            <mat-icon>logout</mat-icon>
            <span>{{ 'menu.user.logout' | translate }}</span>
          </button>
        </div>
      </aside>

      <!-- Content -->
      <section class="settings-content">
        <div class="breadcrumb">
          <span>{{ 'title.account' | translate }}</span>
          <span class="crumb-sep">›</span>
          <span class="crumb-current">{{ activeSectionMeta()?.labelKey | translate }}</span>
        </div>

        <div class="content-scroll">

          <!-- Informations -->
          <div class="settings-panel" *ngIf="activeSection() === 'info'">
            <h2 class="panel-title">{{ 'settings.project.informations' | translate }}</h2>

            <div class="field-stack">
              <div class="field-group">
                <span class="field-label">{{ 'settings.project.profile.changeAvatar' | translate }}</span>

                <div class="avatar-controls">
                  <app-user-avatar class="avatar" [avatarUrl]="user.avatar" [firstName]="user.firstName"
                    [lastName]="user.lastName" [size]="96" [showStatus]="false" [loading]="loadingAvatar"
                    [showUploadProgress]="true">
                  </app-user-avatar>

                  <div class="avatar-buttons">
                    <lib-button (click)="fileInput.click()"
                      tooltip="{{'settings.project.profile.changeAvatar' | translate}}" color="secondary" icon="edit"
                      [disabled]="loadingAvatar !== null && loadingAvatar !== undefined">
                    </lib-button>

                    <lib-button *ngIf="user.avatar" (click)="deleteAvatar()"
                      tooltip="{{'settings.project.profile.deleteAvatar' | translate}}" color="danger" icon="delete"
                      [disabled]="loadingAvatar !== null && loadingAvatar !== undefined">
                    </lib-button>

                    <input formControlName="avatar" hidden (change)="onFileSelected($event)" #fileInput type="file"
                      id="avatar" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" />
                  </div>
                </div>
              </div>

              <div class="field-pair">
                <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
                  <mat-label>{{'form.firstName'|translate}}</mat-label>
                  <input matInput placeholder="{{'form.firstName'|translate}}" formControlName="firstName">
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
                  <mat-label>{{'form.lastName'|translate}}</mat-label>
                  <input matInput placeholder="{{'form.lastName'|translate}}" formControlName="lastName">
                </mat-form-field>
              </div>

              <div class="field-pair">
                <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
                  <mat-label>{{'form.job'|translate}}</mat-label>
                  <input matInput placeholder="{{'form.job'|translate}}" formControlName="job">
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
                  <mat-label>{{'form.company'|translate}}</mat-label>
                  <input matInput placeholder="{{'form.company'|translate}}" formControlName="company">
                </mat-form-field>
              </div>

              <mat-form-field appearance="outline" class="littleForm" subscriptSizing="dynamic">
                <mat-label>{{ 'language.language' | translate }}</mat-label>
                <mat-select formControlName="language" id="language" [fixValue]="form.get('language')?.value">
                  <mat-option *ngFor="let lang of listLanguageOptions" [value]="lang.value">
                    {{ lang.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <!-- Personnalisation -->
          <div class="settings-panel" *ngIf="activeSection() === 'personalization'">
            <h2 class="panel-title">{{ 'settings.project.personalization' | translate }}</h2>
            <p class="panel-desc">{{ 'settings.project.profile.personalization_desc' | translate }}</p>

            <div class="theme-grid">
              <button type="button" class="theme-card" *ngFor="let opt of themeOptions"
                [class.selected]="currentThemeId() === opt.id" (click)="selectTheme(opt.id)">
                <span class="theme-preview" [ngClass]="opt.previewClass">
                  <span class="tp-side"></span>
                  <span class="tp-body">
                    <span class="tp-line" style="width:40%"></span>
                    <span class="tp-line" style="width:70%"></span>
                  </span>
                </span>

                <span class="theme-meta">
                  <span class="theme-name">{{ opt.labelKey | translate }}</span>
                  <mat-icon class="theme-check" *ngIf="currentThemeId() === opt.id">check_circle
                  </mat-icon>
                </span>
              </button>
            </div>

            <p class="theme-instant-hint">
              <mat-icon>bolt</mat-icon>
              {{ 'settings.project.profile.theme_instant_hint' | translate }}
            </p>

            <h2 class="panel-title panel-title-sub">{{ 'settings.project.profile.display_prefs_title' | translate }}</h2>
            <p class="panel-desc">{{ 'settings.project.profile.display_prefs_desc' | translate }}</p>

            <div class="method-cards view-mode-cards">
              <button type="button" class="method-card" [class.selected]="projectsView() === 'card'"
                (click)="setProjectsView('card')">
                <mat-icon>view_module</mat-icon>
                <span class="method-card-title">{{ 'projects.card_view' | translate }}</span>
                <span class="method-card-desc">{{ 'settings.project.profile.view_mode_card_desc' | translate }}</span>
              </button>

              <button type="button" class="method-card" [class.selected]="projectsView() === 'list'"
                (click)="setProjectsView('list')">
                <mat-icon>view_list</mat-icon>
                <span class="method-card-title">{{ 'projects.table_view' | translate }}</span>
                <span class="method-card-desc">{{ 'settings.project.profile.view_mode_list_desc' | translate }}</span>
              </button>
            </div>

            <div class="mfa-row personalization-row">
              <div class="mfa-info">
                <mat-icon class="mfa-icon">archive</mat-icon>

                <div class="mfa-text">
                  <span class="mfa-label">{{ 'settings.project.profile.show_archived_title' | translate }}</span>
                  <p class="mfa-description">{{ 'settings.project.profile.show_archived_desc' | translate }}</p>
                </div>
              </div>

              <mat-slide-toggle [checked]="showArchivedProjectsByDefault()"
                (change)="toggleShowArchivedProjectsByDefault($event.checked)">
              </mat-slide-toggle>
            </div>
          </div>

          <!-- Sécurité -->
          <div class="settings-panel" *ngIf="activeSection() === 'security'">
            <h2 class="panel-title">{{ 'settings.project.security' | translate }}</h2>

            <div class="field-stack">
              <div class="rowEdit">
                <mat-form-field appearance="outline" class="edit" [ngClass]="{'email-table': updateEmail}" subscriptSizing="dynamic">
                  <mat-label>{{'form.email'|translate}}</mat-label>
                  <mat-icon matSuffix>email</mat-icon>
                  <input matInput placeholder="{{'form.email'|translate}}" formControlName="email">
                </mat-form-field>

                <lib-button color="primary" icon="edit" text="{{'form.edit'|translate}}" (click)="editEmail()"
                  *ngIf="!updateEmail">
                </lib-button>
              </div>

              <div class="rowEdit">
                <mat-form-field appearance="outline" class="edit" subscriptSizing="dynamic">
                  <mat-label>{{"form.password" | translate}}</mat-label>
                  <mat-icon matSuffix>lock_outline</mat-icon>
                  <input matInput placeholder="Password" type="password" formControlName="password">
                </mat-form-field>

                <lib-button color="primary" icon="edit" text="{{'form.edit'|translate}}" (click)="editPassword()"
                  *ngIf="!userPwd">
                </lib-button>

                <lib-button color="warning" icon="cancel" text="{{'form.cancel'|translate}}"
                  (click)="resetPassword()" *ngIf="userPwd">
                </lib-button>
              </div>
            </div>

            <div class="auth-methods-badges">
              <span class="auth-method-badge" *ngIf="isAuthMethodAllowed('password')">
                <mat-icon fontIcon="password"></mat-icon>
                {{ 'users.auth_method_password' | translate }}
              </span>

              <span class="auth-method-badge" *ngIf="isAuthMethodAllowed('google')">
                <img src="img/logo/logo_google.svg" alt="">
                {{ 'users.auth_method_google' | translate }}
              </span>

              <span class="auth-method-badge" *ngIf="isAuthMethodAllowed('microsoft')">
                <img src="img/logo/logo_microsoft.png" alt="">
                {{ 'users.auth_method_microsoft' | translate }}
              </span>
            </div>

            <div class="mfa-row" *ngIf="isAuthMethodAllowed('password')" [class.mfa-pending]="pendingFlow !== null"
              [class.mfa-admin-disabled]="user?.mfaEnabled === false">
              <div class="mfa-info">
                <mat-icon class="mfa-icon">{{ user?.mfaEnabled === false ? 'lock' : 'security' }}</mat-icon>

                <div class="mfa-text">
                  <div class="mfa-label-row">
                    <span class="mfa-label">
                      {{ 'settings.project.profile.mfa_title' | translate }}
                    </span>

                    <span class="mfa-method-badge" *ngIf="mfaEnabled && activeMfaMethod && user?.mfaEnabled !== false">
                      <mat-icon>{{ activeMfaMethod === 'totp' ? 'phonelink_lock' : 'email' }}</mat-icon>
                      {{ activeMfaMethod === 'totp' ? ('settings.project.profile.mfa_method_totp' | translate) :
                      ('settings.project.profile.mfa_method_email' | translate) }}
                    </span>
                  </div>

                  <p class="mfa-description" *ngIf="user?.mfaEnabled !== false">
                    <ng-container *ngIf="mfaEnabled && activeMfaMethod === 'totp'">{{
                      'settings.project.profile.mfa_description_totp' | translate }}</ng-container>
                    <ng-container *ngIf="mfaEnabled && activeMfaMethod === 'email'">{{
                      'settings.project.profile.mfa_description_email' | translate }}</ng-container>
                    <ng-container *ngIf="!mfaEnabled || !activeMfaMethod">{{
                      'settings.project.profile.mfa_description' |
                      translate }}</ng-container>
                  </p>

                  <p class="mfa-description mfa-admin-notice" *ngIf="user?.mfaEnabled === false">
                    {{ 'settings.project.profile.mfa_disabled_by_admin' | translate }}
                  </p>
                </div>
              </div>

              <mat-slide-toggle [checked]="mfaEnabled"
                [disabled]="savingMfa || pendingFlow !== null || user?.mfaEnabled === false"
                (change)="toggleMfa($event.checked)">
              </mat-slide-toggle>
            </div>

            <p class="mfa-description mfa-admin-notice" *ngIf="!isAuthMethodAllowed('password')">
              <mat-icon fontIcon="info"></mat-icon>
              {{ 'users.mfa_handled_by_sso' | translate }}
            </p>

            <!-- Sélection de la méthode MFA -->
            <div class="enrollment-form" *ngIf="pendingFlow === 'enroll-select'">
              <div class="enrollment-header">
                <mat-icon>tune</mat-icon>
                <span>{{ 'settings.project.profile.mfa_select_method' | translate }}</span>
              </div>

              <div class="method-cards">
                <button type="button" class="method-card" (click)="selectEnrollMethod('email')"
                  [disabled]="savingMfa">
                  <mat-icon>email</mat-icon>
                  <span class="method-card-title">{{ 'settings.project.profile.mfa_method_email' | translate }}</span>
                  <span class="method-card-desc">{{ 'settings.project.profile.mfa_method_email_desc' | translate
                    }}</span>
                </button>

                <button type="button" class="method-card" (click)="selectEnrollMethod('totp')"
                  [disabled]="savingMfa">
                  <mat-icon>phonelink_lock</mat-icon>
                  <span class="method-card-title">{{ 'settings.project.profile.mfa_method_totp' | translate }}</span>
                  <span class="method-card-desc">{{ 'settings.project.profile.mfa_method_totp_desc' | translate
                    }}</span>
                </button>
              </div>

              <div class="enrollment-actions">
                <lib-button color="none" textColor="primary" icon="close"
                  text="{{ 'settings.project.profile.mfa_cancel' | translate }}" (click)="cancelPending()">
                </lib-button>
              </div>
            </div>

            <!-- Formulaire de confirmation (enroll ou disable) -->
            <div class="enrollment-form" *ngIf="pendingFlow === 'enroll' || pendingFlow === 'disable'">

              <!-- QR code pour TOTP -->
              <div class="totp-qr" *ngIf="pendingQrCodeUrl">
                <img [src]="pendingQrCodeUrl" alt="QR Code MFA" class="qr-img" />

                <div class="totp-secret" *ngIf="pendingSecret">
                  <span class="totp-secret-label">{{ 'settings.project.profile.mfa_manual_secret' | translate
                    }}</span>
                  <code class="totp-secret-value">{{ pendingSecret }}</code>
                </div>
              </div>

              <div class="enrollment-header">
                <mat-icon>{{ pendingMfaMethod === 'totp' ? 'phonelink_lock' : 'mark_email_unread' }}</mat-icon>

                <span *ngIf="pendingFlow === 'enroll' && pendingMfaMethod === 'totp'">
                  {{ 'settings.project.profile.mfa_enter_code_totp' | translate }}
                </span>

                <span *ngIf="pendingFlow === 'enroll' && pendingMfaMethod !== 'totp'">
                  {{ 'settings.project.profile.mfa_enter_code_email' | translate }}
                </span>

                <span *ngIf="pendingFlow === 'disable' && pendingMfaMethod === 'totp'">
                  {{ 'settings.project.profile.mfa_disable_code_totp' | translate }}
                </span>

                <span *ngIf="pendingFlow === 'disable' && pendingMfaMethod !== 'totp'">
                  {{ 'settings.project.profile.mfa_disable_code_email' | translate }}
                </span>
              </div>

              <div class="enrollment-body">
                <mat-form-field appearance="outline" class="code-field" subscriptSizing="dynamic">
                  <mat-label>{{ 'settings.project.profile.mfa_enter_code' | translate }}</mat-label>

                  <input matInput [(ngModel)]="pendingCode" [ngModelOptions]="{standalone: true}" maxlength="6"
                    inputmode="numeric" autocomplete="one-time-code"
                    [disabled]="savingPending || pendingTimeLeft === 0">
                </mat-form-field>

                <div class="enrollment-timer" [class.expired]="pendingTimeLeft === 0">
                  <mat-icon>schedule</mat-icon>
                  <span *ngIf="pendingTimeLeft > 0">{{ pendingTimeLeft }}s</span>
                  <span *ngIf="pendingTimeLeft === 0">{{ 'settings.project.profile.mfa_expired' | translate }}</span>
                </div>
              </div>

              <p class="enrollment-error" *ngIf="pendingError">{{ pendingError }}</p>

              <div class="enrollment-actions">
                <lib-button color="success" icon="check"
                  text="{{ 'settings.project.profile.mfa_confirm' | translate }}"
                  [disabled]="pendingCode.length !== 6 || pendingTimeLeft === 0" [loading]="savingPending"
                  (click)="confirmPending()">
                </lib-button>

                <lib-button color="none" textColor="primary" icon="close"
                  text="{{ 'settings.project.profile.mfa_cancel' | translate }}" [disabled]="savingPending"
                  (click)="cancelPending()">
                </lib-button>
              </div>
            </div>
          </div>

          <!-- Clé API -->
          <div class="settings-panel" *ngIf="activeSection() === 'apikey'">
            <h2 class="panel-title">{{ 'settings.project.api_key' | translate }}</h2>

            <div class="field-stack">
              <div class="field-group apikey-box">
                <mat-form-field appearance="outline" class="inputGen" subscriptSizing="dynamic">
                  <mat-label>{{'settings.project.api_key' | translate}}</mat-label>
                  <input matInput disabled value="{{user.accessKey}}">
                </mat-form-field>

                <lib-button icon="content_copy" color="none" textColor="primary" (click)="copy(user.accessKey)"
                  tooltip="{{'settings.project.copy_key' | translate}}">
                </lib-button>

                <lib-button text="{{'settings.project.genKey' | translate}}" color="secondary" class="generate"
                  icon="key" (click)="genKey()">
                </lib-button>
              </div>
            </div>
          </div>

          <!-- Rapports -->
          <div class="settings-panel" *ngIf="activeSection() === 'reports'">
            <h2 class="panel-title">{{ 'settings.project.reports' | translate }}</h2>

            <div class="field-stack">
              <mat-form-field appearance="outline" class="littleForm" subscriptSizing="dynamic">
                <mat-label>{{'form.reports_subscription'|translate}}</mat-label>
                <mat-select multiple formControlName="subscribeToReport"
                  [fixValue]="form.get('subscribeToReport')?.value">
                  <mat-option value="daily">{{'form.daily'|translate}}</mat-option>
                  <mat-option value="weekly">{{'form.weekly'|translate}}</mat-option>
                  <mat-option value="monthly">{{'form.monthly'|translate}}</mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <fieldset class="mailing-lists" [ngClass]="{ dark: isDarkTheme() }">
              <legend>
                {{ 'projects.mailing_lists' | translate }}
              </legend>

              <div *ngFor="let project of subscribedTo">
                <mat-checkbox [checked]="project.inMailingList" (change)="toggleSubscription($event, project.id)">
                  {{ project.name }}
                </mat-checkbox>
              </div>
            </fieldset>
          </div>

          <!-- Outils dev -->
          <div class="settings-panel" *ngIf="activeSection() === 'debug' && canShowDebugTab()">
            <h2 class="panel-title">{{ 'settings.project.dev_tools' | translate }}</h2>
            <p class="panel-desc">{{ 'settings.project.profile.dev_tools_desc' | translate }}</p>

            <div class="field-stack">
              <mat-form-field appearance="outline" class="littleForm" subscriptSizing="dynamic">
                <mat-label>Rôle debug</mat-label>
                <mat-select [value]="user.role?.name" (selectionChange)="changeRole($event.value)">
                  <mat-option *ngFor="let role of debugRoles" [value]="role">
                    {{ role }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

        </div>

        <div class="save-bar">
          <lib-button color="success" text="{{'form.update' | translate}}" icon="save" (click)="updateProfile()"
            [disabled]="(!form.dirty || form.invalid) && !mailingListChanged()" [loading]="savingProfile">
          </lib-button>
        </div>
      </section>
    </div>
  </form>
</div>
